export interface DroppedFileItem {
  file: File
  /** Directory relative to the drop root, POSIX, no leading slash (e.g. `Contracts/2024`). Empty for root. */
  relativeDir: string
}

export interface DroppedTree {
  items: DroppedFileItem[]
  emptyRelativeDirs: string[]
}

type FileSystemEntryLike = {
  isFile: boolean
  isDirectory: boolean
  name: string
  fullPath?: string
}

type FileSystemFileEntryLike = FileSystemEntryLike & {
  file: (success: (file: File) => void, error?: (err: DOMException) => void) => void
}

type FileSystemDirectoryReaderLike = {
  readEntries: (
    success: (entries: FileSystemEntryLike[]) => void,
    error?: (err: DOMException) => void,
  ) => void
}

type FileSystemDirectoryEntryLike = FileSystemEntryLike & {
  createReader: () => FileSystemDirectoryReaderLike
}

/** Normalize a relative path segment list; reject `..`. Returns POSIX relative path or null. */
export function normalizeRelativePath(value: string): string | null {
  const parts = value
    .replace(/\\/g, '/')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && part !== '.')
  if (parts.some((part) => part === '..')) return null
  return parts.join('/')
}

function asEntryLike(value: unknown): FileSystemEntryLike | null {
  if (!value || typeof value !== 'object') return null
  const entry = value as Partial<FileSystemEntryLike>
  if (typeof entry.isFile !== 'boolean' || typeof entry.isDirectory !== 'boolean' || typeof entry.name !== 'string') {
    return null
  }
  return value as FileSystemEntryLike
}

function asDirectoryEntry(entry: FileSystemEntryLike): FileSystemDirectoryEntryLike | null {
  if (!entry.isDirectory) return null
  const directory = entry as unknown as FileSystemDirectoryEntryLike
  return typeof directory.createReader === 'function' ? directory : null
}

function asFileEntry(entry: FileSystemEntryLike): FileSystemFileEntryLike | null {
  if (!entry.isFile) return null
  const fileEntry = entry as unknown as FileSystemFileEntryLike
  return typeof fileEntry.file === 'function' ? fileEntry : null
}

function fileFromList(file: File): DroppedFileItem | null {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || ''
  if (relative) {
    const normalized = normalizeRelativePath(relative)
    if (normalized === null) return null
    const parts = normalized.split('/').filter(Boolean)
    parts.pop()
    return { file, relativeDir: parts.join('/') }
  }
  return { file, relativeDir: '' }
}

function readAllEntries(reader: FileSystemDirectoryReaderLike): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntryLike[] = []
    const readBatch = () => {
      reader.readEntries(
        (entries) => {
          if (!entries.length) {
            resolve(all)
            return
          }
          all.push(...entries.map((entry) => asEntryLike(entry)).filter((entry): entry is FileSystemEntryLike => Boolean(entry)))
          readBatch()
        },
        reject,
      )
    }
    readBatch()
  })
}

function readFileEntry(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject)
  })
}

async function walkEntry(
  entry: FileSystemEntryLike,
  relativeDir: string,
  items: DroppedFileItem[],
  visitedDirs: Set<string>,
  dirsWithChildren: Set<string>,
): Promise<void> {
  const fileEntry = asFileEntry(entry)
  if (fileEntry) {
    const file = await readFileEntry(fileEntry)
    items.push({ file, relativeDir })
    if (relativeDir) dirsWithChildren.add(relativeDir)
    return
  }

  const directory = asDirectoryEntry(entry)
  if (!directory) return

  const dirPath = relativeDir ? `${relativeDir}/${entry.name}` : entry.name
  const normalizedDir = normalizeRelativePath(dirPath)
  if (normalizedDir === null) return
  visitedDirs.add(normalizedDir)

  const children = await readAllEntries(directory.createReader())
  if (children.length) dirsWithChildren.add(normalizedDir)
  for (const child of children) {
    await walkEntry(child, normalizedDir, items, visitedDirs, dirsWithChildren)
  }
}

/** Collect files (and empty dirs) from a drag-and-drop DataTransfer, preserving relative folders. */
export async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<DroppedTree> {
  const items: DroppedFileItem[] = []
  const visitedDirs = new Set<string>()
  const dirsWithChildren = new Set<string>()

  const entries = Array.from(dataTransfer.items || [])
    .map((item) => {
      const withEntry = item as DataTransferItem & { webkitGetAsEntry?: () => unknown }
      return typeof withEntry.webkitGetAsEntry === 'function' ? asEntryLike(withEntry.webkitGetAsEntry()) : null
    })
    .filter((entry): entry is FileSystemEntryLike => Boolean(entry))

  if (entries.length) {
    for (const entry of entries) {
      // Top-level files land in ""; top-level folders use their own name as the first segment.
      if (entry.isFile) {
        await walkEntry(entry, '', items, visitedDirs, dirsWithChildren)
      } else if (entry.isDirectory) {
        const name = normalizeRelativePath(entry.name)
        if (name === null) continue
        visitedDirs.add(name)
        const directory = asDirectoryEntry(entry)
        if (!directory) continue
        const children = await readAllEntries(directory.createReader())
        if (children.length) dirsWithChildren.add(name)
        for (const child of children) {
          await walkEntry(child, name, items, visitedDirs, dirsWithChildren)
        }
      }
    }
  } else {
    for (const file of Array.from(dataTransfer.files || [])) {
      const item = fileFromList(file)
      if (item) items.push(item)
    }
  }

  const emptyRelativeDirs = [...visitedDirs].filter((dir) => !dirsWithChildren.has(dir)).sort()
  return { items, emptyRelativeDirs }
}

/** Collect files from an <input type="file"> selection (supports webkitdirectory). */
export function collectInputFiles(fileList: FileList | File[] | null | undefined): DroppedTree {
  const items: DroppedFileItem[] = []
  for (const file of Array.from(fileList || [])) {
    const item = fileFromList(file)
    if (item) items.push(item)
  }
  return { items, emptyRelativeDirs: [] }
}

export function uniqueRelativeDirs(items: DroppedFileItem[]): string[] {
  const dirs = new Set<string>()
  for (const item of items) {
    if (!item.relativeDir) continue
    const parts = item.relativeDir.split('/').filter(Boolean)
    let current = ''
    for (const part of parts) {
      current = current ? `${current}/${part}` : part
      dirs.add(current)
    }
  }
  return [...dirs].sort()
}

// Sized for a VPS: fewer round-trips over the public internet. The in-compose
// nginx↔backend network is stable; a single oversized file still uploads alone.
export const UPLOAD_BATCH_MAX_FILES = 40
export const UPLOAD_BATCH_MAX_BYTES = 80 * 1024 * 1024

export function packUploadBatches<T extends { file: File }>(
  items: T[],
  maxFiles = UPLOAD_BATCH_MAX_FILES,
  maxBytes = UPLOAD_BATCH_MAX_BYTES,
): T[][] {
  const batches: T[][] = []
  let current: T[] = []
  let bytes = 0
  for (const item of items) {
    const size = item.file.size
    const wouldExceed =
      current.length > 0 && (current.length >= maxFiles || bytes + size > maxBytes)
    if (wouldExceed) {
      batches.push(current)
      current = []
      bytes = 0
    }
    // A single oversized file still gets its own batch; the server rejects per-file limits.
    current.push(item)
    bytes += size
  }
  if (current.length) batches.push(current)
  return batches
}
